import { useEffect, useState } from "react";
import { BellRing, Loader2, Mail, MessageSquare, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { api, errMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loading, ErrorBox } from "@/components/common";
import { selectCls } from "@/pages/Take5New";

const Check = ({ id, checked, onChange, children }) => (
  <label className="flex min-h-[44px] items-center gap-3 text-sm">
    <input type="checkbox" data-testid={id} className="h-5 w-5 shrink-0 accent-amber-500" checked={checked} onChange={(e) => onChange(e.target.checked)} /> {children}
  </label>
);

export const AlertsSection = () => {
  const [s, setS] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/alert-settings").then((r) => setS(r.data)).catch((e) => setError(errMessage(e)));
  useEffect(() => { load(); }, []);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/alert-settings", { operator_enabled: s.operator_enabled, supervisor_enabled: s.supervisor_enabled, supervisor_delay_minutes: Number(s.supervisor_delay_minutes) });
      setS(data);
      toast.success("Paramètres d'alerte enregistrés.");
    } catch (e) { toast.error(errMessage(e)); } finally { setBusy(false); }
  };
  if (error) return <ErrorBox message={error} onRetry={load} />;
  if (!s) return <Loading />;
  return (
    <div className="max-w-2xl space-y-3 rounded-xl border border-border bg-card p-5" data-testid="alerts-settings">
      <div>
        <h3 className="flex items-center gap-2 text-lg font-semibold"><BellRing className="h-5 w-5 text-amber-500" /> Alertes de dépassement de temps</h3>
        <p className="mt-1 text-sm text-muted-foreground">Quand un Take 5 autorisé dépasse sa fin prévue sans être terminé ni arrêté. Vérification toutes les 15 minutes.</p>
      </div>
      <Check id="alerts-operator-enabled" checked={s.operator_enabled} onChange={(v) => setS({ ...s, operator_enabled: v })}>
        Prévenir l'opérateur à l'heure de fin prévue
      </Check>
      <div>
        <Check id="alerts-supervisor-enabled" checked={s.supervisor_enabled} onChange={(v) => setS({ ...s, supervisor_enabled: v })}>
          Prévenir tous les superviseurs du workplace (et le chef d'équipe d'un Take 5 collectif)
        </Check>
        <label htmlFor="alerts-delay" className="mt-2 block text-sm font-semibold">Délai après la fin prévue avant l'alerte superviseur</label>
        <select id="alerts-delay" data-testid="alerts-supervisor-delay" disabled={!s.supervisor_enabled} className={`${selectCls} mt-1.5 sm:w-60`}
          value={s.supervisor_delay_minutes} onChange={(e) => setS({ ...s, supervisor_delay_minutes: e.target.value })}>
          {s.delays.map((m) => <option key={m} value={m}>{m < 60 ? `${m} min` : `${Math.floor(m / 60)} h${m % 60 ? ` ${m % 60}` : ""}`}</option>)}
        </select>
      </div>
      <div>
        <p className="text-sm font-semibold">Moyen d'alerte</p>
        <div className="mt-2 flex flex-wrap gap-2 text-sm">
          <span data-testid="alerts-channel-email" className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/60 bg-amber-500/10 px-3 py-1.5 font-semibold"><Mail className="h-4 w-4" /> E-mail (actif)</span>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-muted-foreground"><MessageSquare className="h-4 w-4" /> SMS (bientôt)</span>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-muted-foreground"><Smartphone className="h-4 w-4" /> Notification push (application mobile)</span>
        </div>
      </div>
      <Button data-testid="alerts-save" onClick={save} disabled={busy} className="h-11">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Enregistrer</Button>
    </div>
  );
};
